import deepCopy from 'lib/deepCopy';
import {OnboardingUnitTypeAndUnitsData} from 'units/UnitDictionaryDAO';
import {UnitTypesQueryUnitTypeNode} from 'units/UnitsConstants';
import {listToReadable} from 'util/NameUtil';

export interface UnitsDependencyDataStructures {
  // Note that only the "dependency" data structures are fully populated. The "dependent" data structures only store the direct relationships.
  unitTypeToDependencyUnits: {[unitTypeName: string]: Array<string>}; // eg: {"molar concentration": ["mol", "L", "dm"]}
  unitTypeToDependencyUnitTypes: {[unitTypeName: string]: Array<string>}; // eg: {"area": ["length"], "volume": ["length"]}

  unitToDependentUnitTypes: {[unitSymbol: string]: Array<string>};
  unitTypeToDependentUnitTypes: {[unitTypeName: string]: Array<string>}; // eg: {"length": ["area", "volume"]}
  unitSymbolToUnitTypeName: {[unitSymbol: string]: string};
}

function convertSetsToArrays(dataStructure: {[key: string]: Set<string>}): {[key: string]: Array<string>} {
  const result: {[key: string]: Array<string>} = {};
  for (const key of Object.keys(dataStructure)) {
    result[key] = Array.from(dataStructure[key]);
  }
  return result;
}

export function getUnitsDependencyDataStructures(
  originalDefaultUnitsData: Array<OnboardingUnitTypeAndUnitsData>
): UnitsDependencyDataStructures {
  const defaultUnitsData = deepCopy(originalDefaultUnitsData);
  const sortedDefaultUnitsData = defaultUnitsData.sort((a, b) => a.rank - b.rank);
  const unitSymbolToUnitTypeName: {[unitSymbol: string]: string} = {};
  for (const unitTypeAndUnitsData of sortedDefaultUnitsData) {
    const {unitType, units} = unitTypeAndUnitsData;
    const unitTypeName = unitType.name;
    for (const unit of units) {
      unitSymbolToUnitTypeName[unit.symbol] = unitTypeName;
    }
  }
  const unitToDependentUnitTypes: {[unitSymbol: string]: Set<string>} = {}; // eg: {"mol": ["molar concentration"]}
  const unitTypeToDependentUnitTypes: {[unitTypeName: string]: Set<string>} = {}; // eg: {"length": ["area", "volume"]}
  const unitTypeToDependencyUnitTypes: {[unitTypeName: string]: Set<string>} = {}; // eg: {"area": ["length"], "volume": ["length"]}
  const unitTypeToDependencyUnits: {[unitTypeName: string]: Set<string>} = {}; // eg: {"molar concentration": ["mol", "L", "dm"]}

  for (const unitTypeAndUnitsData of sortedDefaultUnitsData) {
    const {unitType, formula} = unitTypeAndUnitsData;
    const unitTypeName = unitType.name;
    if (formula == null) {
      continue;
    }

    for (const {value: formulaTermUnit} of formula) {
      const formulaTermUnitType = unitSymbolToUnitTypeName[formulaTermUnit];

      if (unitToDependentUnitTypes.hasOwnProperty(formulaTermUnit)) {
        unitToDependentUnitTypes[formulaTermUnit].add(unitTypeName);
      } else {
        unitToDependentUnitTypes[formulaTermUnit] = new Set([unitTypeName]);
      }

      if (unitTypeToDependentUnitTypes.hasOwnProperty(formulaTermUnitType)) {
        unitTypeToDependentUnitTypes[formulaTermUnitType].add(unitTypeName);
      } else {
        unitTypeToDependentUnitTypes[formulaTermUnitType] = new Set([unitTypeName]);
      }

      if (unitTypeToDependencyUnitTypes.hasOwnProperty(unitTypeName)) {
        unitTypeToDependencyUnitTypes[unitTypeName].add(formulaTermUnitType);
      } else {
        unitTypeToDependencyUnitTypes[unitTypeName] = new Set([formulaTermUnitType]);
      }

      if (unitTypeToDependencyUnits.hasOwnProperty(unitTypeName)) {
        unitTypeToDependencyUnits[unitTypeName].add(formulaTermUnit);
      } else {
        unitTypeToDependencyUnits[unitTypeName] = new Set([formulaTermUnit]);
      }

      // Fill in with nested dependencies as well, which should work given that sortedDefaultUnitsData is sorted by rank.
      if (unitTypeToDependencyUnitTypes.hasOwnProperty(formulaTermUnitType)) {
        for (const nestedDependency of unitTypeToDependencyUnitTypes[formulaTermUnitType]) {
          unitTypeToDependencyUnitTypes[unitTypeName].add(nestedDependency);
        }
      }
      if (unitTypeToDependencyUnits.hasOwnProperty(formulaTermUnitType)) {
        for (const nestedDependency of unitTypeToDependencyUnits[formulaTermUnitType]) {
          unitTypeToDependencyUnits[unitTypeName].add(nestedDependency);
        }
      }
    }
  }

  return {
    unitToDependentUnitTypes: convertSetsToArrays(unitToDependentUnitTypes),
    unitTypeToDependentUnitTypes: convertSetsToArrays(unitTypeToDependentUnitTypes),
    unitTypeToDependencyUnitTypes: convertSetsToArrays(unitTypeToDependencyUnitTypes),
    unitTypeToDependencyUnits: convertSetsToArrays(unitTypeToDependencyUnits),
    unitSymbolToUnitTypeName,
  };
}

// Handles dependent unitTypes and units when selecting a new unitType.
// Note that this function will not deselect any units, even if they are no longer required.
export function getNewSelectedUnitTypesAndUnits(
  unitsDependencyDataStructures: UnitsDependencyDataStructures,
  selectedUnitTypeNames: Array<string>,
  selectedUnitSymbols: Array<string>,
  unitTypeName: string,
  selected: boolean
): {
  newSelectedUnitTypeNames: Array<string>;
  newSelectedUnitSymbols: Array<string>;
} {
  const {unitTypeToDependencyUnitTypes, unitTypeToDependencyUnits} = unitsDependencyDataStructures;

  const newSelectedUnitTypeNames = new Set(selectedUnitTypeNames);
  if (selected) {
    newSelectedUnitTypeNames.add(unitTypeName);
    if (unitTypeToDependencyUnitTypes.hasOwnProperty(unitTypeName)) {
      for (const dependentUnitTypeName of unitTypeToDependencyUnitTypes[unitTypeName]) {
        newSelectedUnitTypeNames.add(dependentUnitTypeName);
      }
    }
  } else {
    newSelectedUnitTypeNames.delete(unitTypeName);
  }

  const newSelectedUnitSymbols = new Set(selectedUnitSymbols);
  if (selected) {
    if (unitTypeToDependencyUnits.hasOwnProperty(unitTypeName)) {
      for (const unitSymbol of unitTypeToDependencyUnits[unitTypeName]) {
        newSelectedUnitSymbols.add(unitSymbol);
      }
    }
  }
  return {
    newSelectedUnitTypeNames: Array.from(newSelectedUnitTypeNames),
    newSelectedUnitSymbols: Array.from(newSelectedUnitSymbols),
  };
}

export function getUnitTypeForceSelectedText(
  unitTypeData: OnboardingUnitTypeAndUnitsData,
  selectedUnitTypeNames: Array<string>,
  unitsDependencyDataStructures: UnitsDependencyDataStructures,
  existingUnitTypes: Array<UnitTypesQueryUnitTypeNode>
): string | null {
  const {unitTypeToDependentUnitTypes} = unitsDependencyDataStructures;
  const {unitType} = unitTypeData;
  const otherUnitTypesThatRequireThisUnitType = unitTypeToDependentUnitTypes.hasOwnProperty(unitType.name)
    ? unitTypeToDependentUnitTypes[unitType.name].filter((name) => selectedUnitTypeNames.includes(name))
    : [];
  const alreadyExists = existingUnitTypes.some((existingUnitType) => existingUnitType.name === unitType.name);
  const forceSelectedText = alreadyExists
    ? 'This unit type already exists'
    : otherUnitTypesThatRequireThisUnitType.length > 0
      ? `This unit type is required by ${listToReadable(otherUnitTypesThatRequireThisUnitType)}`
      : null;
  return forceSelectedText;
}

export function isUnitRequiredByOtherSelectedUnitTypes(
  unitSymbol: string,
  selectedUnitTypeNames: Array<string>,
  unitsDependencyDataStructures: UnitsDependencyDataStructures
): boolean {
  const {unitToDependentUnitTypes} = unitsDependencyDataStructures;
  return (
    unitToDependentUnitTypes.hasOwnProperty(unitSymbol) &&
    unitToDependentUnitTypes[unitSymbol].some((unitTypeName) => selectedUnitTypeNames.includes(unitTypeName))
  );
}
